import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { CommonButton, CommonTextFieldV1 } from 'gemlux-common-components';
import TickSvg from '../../../assets/img/tick.svg';
import AddIcon from '../../../assets/img/V1/add-icon.svg';
import WarningSvg from '../../../assets/img/V1/red-warning.svg';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { authActions } from '../../../redux/auth/authSlice';
import { GemstoneRole } from '../../../utils/enums/gemstone-role.enum';
import { theme } from '../../../theme/theme';
import LogoLink from '../../LogoLink/LogoLink';

export const roles = [
  GemstoneRole.GEM_TRADER,
  GemstoneRole.DESIGNER,
  GemstoneRole.CUTTER,
  GemstoneRole.ACADEMICIAN,
  GemstoneRole.GEMOLOGIST,
  GemstoneRole.MINER,
  GemstoneRole.RETAILER_BRAND,
  GemstoneRole.OTHER,
];

export type IRoleProps = {
  setActiveStep: (_activeStep: number) => void;
};

function Role({ setActiveStep }: IRoleProps) {
  // Ensure previousRoles is always an array
  const previousRoles = useAppSelector((state) => state.auth.signUpData.roles) || [];

  const [selectedRoles, setSelectedRoles] = useState<GemstoneRole[]>(
    previousRoles.filter((r) => Object.values(GemstoneRole).includes(r as GemstoneRole)) as GemstoneRole[],
  );

  const [otherRole, setOtherRole] = useState<string>(
    previousRoles.find((r) => !Object.values(GemstoneRole).includes(r as GemstoneRole)) || '',
  );

  const dispatch = useAppDispatch();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const handleRoleClick = (role: GemstoneRole) => {
    setSelectedRoles((prevRoles: GemstoneRole[]) =>
      prevRoles.includes(role) ? prevRoles.filter((r: GemstoneRole) => r !== role) : [...prevRoles, role],
    );
    if (role === GemstoneRole.OTHER && selectedRoles.includes(GemstoneRole.OTHER)) {
      setOtherRole('');
    }
  };

  const handleOtherRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtherRole(e.target.value);
  };

  const handleContinue = () => {
    dispatch(authActions.setSignUpData({ lastActiveStep: 5, roles: [...selectedRoles, otherRole] }));
    setActiveStep(5);
  };

  const isOtherSelected = selectedRoles.includes(GemstoneRole.OTHER);
  const isContinueDisabled = selectedRoles.length === 0 || (isOtherSelected && otherRole.trim() === '');

  return (
    <Grid
      display="flex"
      flexDirection="column"
      gap={6}
      alignItems="center"
      maxWidth="700px"
      sx={{
        backgroundColor: '#FFF',
        padding: '10%',
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap="10px">
        <LogoLink padding="5%" width="173.343px" height="32px" />
      </Box>
      <Grid display="flex" flexDirection="column" gap={1} alignItems="center">
        <Typography variant="textH3" textAlign="center" padding="24px 84px 24px 84px">
          Welcome to Porolis GemLUX® sign-on journey.{' '}
        </Typography>
        <Typography variant="calloutInterRegular" textAlign="center" padding="0px 24px 0px 24px">
          Which roles best describe your position in the gemstone and jewellery industry? (Select all that apply)
        </Typography>
      </Grid>
      <Grid
        container
        spacing="10px"
        alignItems="center"
        justifyContent="center"
        maxWidth={isSmallScreen ? '100%' : '100%'}
      >
        {roles.map((role: GemstoneRole) => (
          <Grid item key={role}>
            <CommonButton
              aria-pressed={selectedRoles.includes(role) ? 'false' : 'true'}
              padding="12px 10px"
              radius="8px"
              onClick={() => handleRoleClick(role)}
              sx={{
                fontWeight: 400,
                fontSize: '15px',
                fontFamily: 'Inter',
                color: '#14181F',
                border: `1px solid ${selectedRoles.includes(role) ? '#3348FF' : '#B6C1CA'}`,
                backgroundColor: selectedRoles.includes(role) ? '#E0EDFF' : 'transparent',
              }}
              startIcon={
                selectedRoles.includes(role) ? (
                  <Box component="img" src={TickSvg} alt="Selected" />
                ) : (
                  <Box component="img" src={AddIcon} alt="Add" />
                )
              }
            >
              {role}
            </CommonButton>
          </Grid>
        ))}
      </Grid>
      {isOtherSelected && (
        <Box width="100%" mt={2} display="flex" flexDirection="column" alignItems="center" gap={1.5}>
          <Typography variant="textBase500">Mention other</Typography>
          <CommonTextFieldV1
            size="small"
            value={otherRole}
            onChange={handleOtherRoleChange}
            error={isOtherSelected && otherRole.trim() === ''}
            helperText={
              isOtherSelected && otherRole.trim() === '' ? (
                <Box display="flex" alignItems="flex-start" gap="6px" marginLeft="none" alignContent="flex-start">
                  <Box component="img" src={WarningSvg} alt="Warning icon" />
                  <Typography
                    variant="textSM"
                    sx={{
                      color: '#DF0C3D',
                    }}
                  >
                    Please specify your role.
                  </Typography>
                </Box>
              ) : (
                ''
              )
            }
            sx={{
              borderRadius: '8px',
              width: '70%',
            }}
          />
        </Box>
      )}
      <CommonButton
        variant="contained"
        padding="12px 32px"
        disabled={isContinueDisabled}
        onClick={handleContinue}
        sx={{
          backgroundColor: isContinueDisabled ? '#EDF0F2' : '#3348FF',
          fontFamily: 'Inter',
          fontSize: '15px',
          fontWeight: 500,
          mb: '10px',
          '&:disabled': {
            backgroundColor: '#EDF0F2',
            color: '#14181F',
          },
        }}
      >
        Continue
      </CommonButton>
    </Grid>
  );
}

export default Role;
